import React, { useState, useCallback, useEffect } from "react";

interface ResizableDividerProps {
  topComponent: React.ReactNode;
  bottomComponent: React.ReactNode;
  initialTopHeight?: number;
  minTopHeight?: number;
  maxTopHeight?: number;
  className?: string;
}

export const ResizableDivider: React.FC<ResizableDividerProps> = ({
  topComponent,
  bottomComponent,
  initialTopHeight = 50,
  minTopHeight = 0,
  maxTopHeight = 100,
  className = "",
}) => {
  const [topHeight, setTopHeight] = useState(initialTopHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startTopHeight, setStartTopHeight] = useState(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setStartY(e.clientY);
      setStartTopHeight(topHeight);
      e.preventDefault();
    },
    [topHeight]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const container = document.querySelector(".resizable-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const deltaY = e.clientY - startY;
      const deltaPercentage = (deltaY / containerRect.height) * 100;

      let newTopHeight = startTopHeight + deltaPercentage;
      newTopHeight = Math.max(
        minTopHeight,
        Math.min(maxTopHeight, newTopHeight)
      );

      setTopHeight(newTopHeight);
    },
    [isDragging, startY, startTopHeight, minTopHeight, maxTopHeight]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      document.addEventListener("mouseup", handleMouseUp, { capture: true });

      const style = document.createElement("style");
      style.id = "resize-cursor-style";
      style.textContent = `
        * {
          cursor: row-resize !important;
        }
        body {
          cursor: row-resize !important;
        }
      `;
      document.head.appendChild(style);

      document.body.style.userSelect = "none";
      document.body.style.pointerEvents = "none";

      const overlay = document.createElement("div");
      overlay.id = "resize-overlay";
      overlay.style.position = "fixed";
      overlay.style.top = "0";
      overlay.style.left = "0";
      overlay.style.width = "100%";
      overlay.style.height = "100%";
      overlay.style.zIndex = "9999";
      overlay.style.cursor = "row-resize !important";
      overlay.style.backgroundColor = "transparent";
      document.body.appendChild(overlay);
    } else {
      document.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });

      const style = document.getElementById("resize-cursor-style");
      if (style) {
        document.head.removeChild(style);
      }

      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";

      const overlay = document.getElementById("resize-overlay");
      if (overlay) {
        document.body.removeChild(overlay);
      }
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });

      const style = document.getElementById("resize-cursor-style");
      if (style) {
        document.head.removeChild(style);
      }

      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";

      const overlay = document.getElementById("resize-overlay");
      if (overlay) {
        document.body.removeChild(overlay);
      }
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className={`flex flex-col h-full resizable-container ${className}`}>
      <div
        className="overflow-hidden"
        style={{ height: `${topHeight}%`, minHeight: "0" }}
      >
        {topComponent}
      </div>

      <div
        className="flex items-center justify-center h-[0.063rem] bg-border cursor-row-resize transition-colors relative group flex-shrink-0"
        onMouseDown={handleMouseDown}
      >
        <div className="w-8 h-0.5 bg-grey rounded-full group-hover:bg-white transition-colors"></div>
      </div>

      <div
        className="overflow-hidden"
        style={{ height: `${100 - topHeight}%`, minHeight: "0" }}
      >
        {bottomComponent}
      </div>
    </div>
  );
};
